// Điểm mở rộng thuần dữ liệu mà mỗi pages/<m>/meta.ts khai báo cho khung ứng dụng (FSD.md §2.1) + phần dựng kết quả
// Tìm nhanh (thuần, để kiểm thử). Shell không biết nghiệp vụ module.
import type { ComponentType } from 'react';
import { fold, type ModuleDef } from '@cathouse/contracts';
import type { IconName } from '@/shared/ui';

/** Mục "Tạo mới" (menu thanh trên + nhóm "Tạo mới" của Tìm nhanh). */
export interface CreateItem {
  label: string;
  icon: IconName;
  module: string;
  perm?: string;
  to: string;
  search?: Record<string, unknown>;
  /** Mở lớp phủ tại chỗ (giữ trang hiện tại): navigate({ to: '.', search: s => ({ ...s, [key]: value }) }). */
  overlay?: readonly [key: string, value: string];
  /** Gợi ý ngắn hiển thị bên phải mục menu (vắng ⇒ hiện phím tắt). */
  hint?: string;
  /** Phím tắt một chữ (VD 'n'): bấm khi không gõ trong ô nhập và không có lớp nổi nào mở ⇒ chạy mục này. */
  shortcut?: string;
}

/** Một kết quả của nguồn tìm: điều hướng (to + search) hoặc mở lớp phủ tại chỗ (overlay). */
export interface SearchHit {
  key: string;
  icon: IconName;
  title: string;
  sub?: string;
  meta?: string;
  to?: string;
  search?: Record<string, unknown>;
  overlay?: readonly [string, string];
}
export interface SearchContext {
  signal: AbortSignal;
  /** Module có hiển thị với người dùng (dùng trước khi tạo kết quả dẫn sang module khác). */
  visible: (moduleId: string) => boolean;
}
/** Nguồn kết quả cho Tìm nhanh (Ctrl K). Service tự lọc theo phạm vi quyền. */
export interface SearchProvider {
  id: string;
  module: string;
  /** Tiêu đề nhóm trong danh sách kết quả. */
  group: string;
  /** Cần ít nhất một quyền trong danh sách. */
  perm?: string[];
  /** Số ký tự tối thiểu (mặc định 2). */
  minLength?: number;
  search: (q: string, ctx: SearchContext) => Promise<SearchHit[]>;
}
/** Phần tử nhỏ trên thanh trên, đặt trước chuông (VD pill chấm công). */
export interface TopbarItem {
  id: string;
  module: string;
  perm?: string[];
  component: ComponentType;
}

export const MAX_HITS_PER_PROVIDER = 6;
export const SEARCH_DEBOUNCE_MS = 200;
export const DEFAULT_MIN_LENGTH = 2;

/** Mục được phép: module hiển thị ∧ (không yêu cầu quyền ∨ có ít nhất một quyền). */
export function permitted(x: { module: string; perm?: string | string[] }, visible: (moduleId: string) => boolean, any: (...perms: string[]) => boolean) {
  if (!visible(x.module)) return false;
  const perms = x.perm == null ? [] : Array.isArray(x.perm) ? x.perm : [x.perm];
  return !perms.length || any(...perms);
}

export interface PaletteEntry {
  id: string;
  group: string;
  icon: IconName;
  title: string;
  sub?: string;
  meta?: string;
  to?: string;
  search?: Record<string, unknown>;
  overlay?: readonly [string, string];
}

/**
 * Danh sách Tìm nhanh như prototype: "Đi tới" (module khớp fold), "Tạo mới" (đã lọc quyền, khớp fold), rồi kết quả từng
 * nguồn (tối đa 6/nguồn). Chưa gõ gì ⇒ 12 mục đầu.
 */
export function paletteEntries(q: string, modules: readonly ModuleDef[], createItems: readonly CreateItem[], results: readonly { provider: SearchProvider; hits: readonly SearchHit[] }[]): PaletteEntry[] {
  const fq = fold(q.trim());
  const out: PaletteEntry[] = [];
  for (const m of modules) if (!fq || fold(m.label).includes(fq)) out.push({ id: `go:${m.id}`, group: 'Đi tới', icon: m.icon, title: m.label, to: `/${m.route}` });
  for (const c of createItems)
    if (!fq || fold(c.label).includes(fq)) out.push({ id: `new:${c.module}:${c.label}`, group: 'Tạo mới', icon: c.icon || 'plus', title: c.label, to: c.to, search: c.search, overlay: c.overlay });
  if (fq)
    for (const { provider, hits } of results)
      for (const h of hits.slice(0, MAX_HITS_PER_PROVIDER)) out.push({ ...h, id: `${provider.id}:${h.key}`, group: provider.group, icon: h.icon || 'right' });
  return fq ? out : out.slice(0, 12);
}
